import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Badge, Button, Card, DataTable, EmptyState, Icon, PetalField, StatCard, type Column } from '../../design';
import { useAuth } from '../../auth/AuthProvider';
import { clientsApi, clientStatusTone } from '../../lib/clients';
import { formatDuration, formatTime, isThisWeek, isToday, sessionsApi } from '../../lib/sessions';
import { CLIENT_STATUS_LABELS, type ClientListItem, type SessionItem } from '../../lib/types';

export function OverviewScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [playing, setPlaying] = useState(false);

  const canSeeClients = user?.role === 'admin' || user?.role === 'practitioner';
  const isClient = user?.role === 'client';
  const [clients, setClients] = useState<ClientListItem[]>([]);
  const [loading, setLoading] = useState(canSeeClients);
  const [sessions, setSessions] = useState<SessionItem[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);

  useEffect(() => {
    if (!canSeeClients) return;
    void clientsApi
      .list()
      .then((r) => setClients(r.clients))
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, [canSeeClients]);

  useEffect(() => {
    // Role-scoped server-side: a client gets their own sessions here.
    void sessionsApi
      .list()
      .then((r) => setSessions(r.sessions))
      .catch(() => undefined)
      .finally(() => setSessionsLoading(false));
  }, []);

  const activeCount = clients.filter((c) => c.status === 'active').length;
  const weekCount = sessions.filter((s) => s.status !== 'cancelled' && isThisWeek(s.startsAt)).length;
  const today = sessions
    .filter((s) => s.status !== 'cancelled' && isToday(s.startsAt))
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt))
    .slice(0, 3);

  const columns: Column<ClientListItem>[] = [
    {
      key: 'name',
      header: 'Client',
      render: (r) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
          <Avatar name={r.name} size="sm" />
          <span>
            <span style={{ color: 'var(--text-strong)' }}>{r.name}</span>
            <br />
            <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)' }}>{r.goal ?? '—'}</span>
          </span>
        </span>
      ),
    },
    { key: 'program', header: 'Program', render: (r) => r.program ?? <span style={{ color: 'var(--text-muted)' }}>—</span> },
    {
      key: 'status',
      header: 'Status',
      align: 'right',
      render: (r) => (
        <Badge dot tone={clientStatusTone(r.status)}>
          {CLIENT_STATUS_LABELS[r.status]}
        </Badge>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 'var(--space-6)' }}>
        {/* "Active clients" and "Sessions this week" are real. The other two
            stay mock until playback and payments exist — and the tile set is
            practitioner-shaped for client-role users; per-role tiles are a
            flagged follow-up, not part of the sessions module. */}
        <StatCard label="Active clients" value={loading ? '—' : String(activeCount)} icon="users" />
        <StatCard label="Sessions this week" value={sessionsLoading ? '—' : String(weekCount)} icon="calendar" />
        <StatCard label="Programs listened" value="1,904" delta="steady" direction="flat" icon="headphones" />
        <StatCard label="Revenue" value="$8,420" delta="+6%" direction="up" icon="trending-up" footnote="August" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 'var(--space-6)', alignItems: 'start' }}>
        {canSeeClients && (
          <Card
            pad="none"
            title="Clients in progress"
            action={
              <Button variant="ghost" size="sm" iconAfter="arrow-right" onClick={() => navigate('/clients')}>
                View all
              </Button>
            }
          >
            {loading ? (
              <div style={{ padding: 'var(--space-6)', color: 'var(--text-muted)' }}>Loading…</div>
            ) : clients.length === 0 ? (
              <EmptyState icon="users" title="No clients yet">
                Add your first client from the Clients screen.
              </EmptyState>
            ) : (
              <DataTable
                onRowClick={(r) => navigate(`/clients/${r.id}`)}
                rows={clients.slice(0, 5)}
                columns={columns}
              />
            )}
          </Card>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
          <div style={{ position: 'relative', overflow: 'hidden', borderRadius: 'var(--radius-lg)' }}>
            <Card variant="brand" pad="lg">
              <PetalField size={260} blur={2} style={{ position: 'absolute', right: -60, top: -70 }} />
              <div style={{ position: 'relative' }}>
                <div style={{ font: 'var(--type-label)', letterSpacing: 'var(--track-label)', textTransform: 'uppercase', color: 'var(--navy-200)' }}>
                  Now playing
                </div>
                <div style={{ font: 'var(--fw-light) 30px/1.2 var(--font-display)', color: 'var(--white)', margin: '10px 0 4px' }}>Deep Sleep</div>
                <div style={{ font: 'var(--type-body-sm)', color: 'var(--navy-200)', marginBottom: 20 }}>Ultrasonic · 30 min · 412 listeners</div>
                <Button variant="inverse" size="sm" icon={playing ? 'pause' : 'play'} onClick={() => setPlaying(!playing)}>
                  {playing ? 'Pause preview' : 'Play preview'}
                </Button>
              </div>
            </Card>
          </div>

          <Card title="Today" action={<Icon name="calendar" size="16px" style={{ color: 'var(--navy-300)' }} />} pad="sm">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
              {sessionsLoading ? (
                <span style={{ font: 'var(--type-body-sm)', color: 'var(--text-muted)' }}>Loading…</span>
              ) : today.length === 0 ? (
                // A full EmptyState overwhelms this small card; a muted line
                // matches its scale (same reasoning as the loading text).
                <span style={{ font: 'var(--type-body-sm)', color: 'var(--text-muted)' }}>No sessions today.</span>
              ) : (
                today.map((s) => (
                  <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
                    <span style={{ width: 6, height: 36, borderRadius: 999, background: 'var(--navy-300)' }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ font: 'var(--type-h4)', color: 'var(--text-strong)' }}>
                        {isClient ? s.title ?? 'Session' : s.client?.name ?? s.attendeeName ?? 'Unknown'}
                      </div>
                      <div style={{ font: 'var(--type-caption)', color: 'var(--text-muted)' }}>
                        {isClient
                          ? formatDuration(s.startsAt, s.endsAt)
                          : `${s.title ?? 'Session'} · ${formatDuration(s.startsAt, s.endsAt)}`}
                      </div>
                    </div>
                    <span style={{ marginLeft: 'auto', font: 'var(--type-caption)', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {formatTime(s.startsAt)}
                    </span>
                  </div>
                ))
              )}
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
